<template>
  <div class="container sn-mycar-info">
    <!-- 查询区域 -->
    <el-card shadow="never" class="search-card">
      <el-form :inline="true" :model="query" @submit.prevent="handleSearch">
        <el-form-item label="序列号" style="flex: 1;">
          <el-input
            v-model="query.serialNo"
            placeholder="请输入12位序列号"
            maxlength="12"
            @keyup.enter="handleSearch"
          />
        </el-form-item>

        <el-form-item>
          <el-button type="primary" class="search-btn" @click="handleSearch" :loading="loading">
            查询
          </el-button>
          <el-button class="reset-btn" @click="resetSearch">重置</el-button>
        </el-form-item>
      </el-form>

      <!-- 最近查询记录 -->
      <div class="recent-searches" v-if="recentSerials.length > 0">
        <span class="recent-label">最近查询：</span>
        <el-tag
          v-for="(sn, index) in recentSerials"
          :key="index"
          size="small"
          class="recent-tag"
          :type="sn === query.serialNo ? 'primary' : 'info'"
          effect="plain"
          @click="selectRecentSerial(sn)"
        >
          {{ sn }}
        </el-tag>
        <el-button type="danger" link size="small" @click="clearRecentSerials" class="clear-btn">
          清空记录
        </el-button>
      </div>
    </el-card>

    <!-- 结果展示 -->
    <el-card v-if="data" shadow="hover" class="result-card">
      <template #header>
        <div class="header">
          <div class="header-left">
            <span class="title">设备信息</span>
            <el-tag size="small" type="info" effect="plain" class="serial-tag">
              序列号: {{ data.serialNo }}
            </el-tag>
          </div>
          <el-tag :type="getStatusType(data.pdtState)" size="large" effect="dark">
            {{ getStatusText(data.pdtState) }}
          </el-tag>
        </div>
      </template>

      <el-descriptions :column="2" border>
        <el-descriptions-item label="产品型号">
          <div class="description-value">{{ data.pdtType }}</div>
        </el-descriptions-item>

        <el-descriptions-item label="配置名称" :span="2">
          <div class="description-value">{{ data.confName }}</div>
        </el-descriptions-item>

        <el-descriptions-item label="经销商名称">
          <div class="description-value">{{ data.venderName }}</div>
        </el-descriptions-item>

        <el-descriptions-item label="经销商编码">
          <div class="description-value">{{ data.venderCode }}</div>
        </el-descriptions-item>

        <el-descriptions-item label="分公司名称">
          <div class="description-value">{{ data.filialeName }}</div>
        </el-descriptions-item>

        <el-descriptions-item label="分公司编码">
          <div class="description-value">{{ data.filialeCode }}</div>
        </el-descriptions-item>

        <el-descriptions-item label="国家/地区">
          <div class="description-value">
            {{ data.countryCn }} ({{ data.countryEn }}) 
            <el-tag size="small" type="info" effect="plain">{{ data.countryCode }}</el-tag>
          </div>
        </el-descriptions-item>

        <el-descriptions-item label="ERP编码">
          <div class="description-value">{{ data.erpCode }}</div>
        </el-descriptions-item>

        <el-descriptions-item label="升级状态">
          <el-tag :type="data.upgradeFlag === 1 ? 'warning' : 'info'" size="small">
            {{ data.upgradeFlag === 1 ? '已升级' : '未升级' }}
          </el-tag>
        </el-descriptions-item>
      </el-descriptions>
    </el-card>

    <!-- 空状态 -->
    <el-empty v-else-if="!loading" description="暂无数据，请输入序列号查询" />
  </div>
</template>

<script setup>
import { reactive, ref, onMounted, watch } from 'vue'
import { ElMessage } from 'element-plus'
import { getSomeOne } from '../api/users'

const loading = ref(false)

// 查询参数
const query = reactive({
  serialNo: ''
})

// 返回数据
const data = ref(null)

// 最近查询记录（最多5条）
const recentSerials = ref([])

// 存储键名
const STORAGE_KEY = 'recent_serial_searches'

// 从localStorage加载最近查询记录
const loadRecentSerials = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY)
    if (saved) {
      recentSerials.value = JSON.parse(saved)
    }
  } catch (err) {
    console.error('加载最近查询记录失败:', err)
  }
}

// 保存最近查询记录到localStorage
const saveRecentSerials = () => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(recentSerials.value))
  } catch (err) {
    console.error('保存最近查询记录失败:', err)
  }
}

// 添加序列号到最近查询记录
const addToRecentSerials = (serialNo) => {
  if (!serialNo) return
  
  // 过滤掉空值，并去重
  let updated = recentSerials.value.filter(sn => sn && sn !== serialNo)
  
  // 将新的序列号添加到开头
  updated.unshift(serialNo)
  
  // 只保留最近5条
  recentSerials.value = updated.slice(0, 5)
  
  // 保存到localStorage
  saveRecentSerials()
}

// 选择最近查询的序列号
const selectRecentSerial = (serialNo) => {
  query.serialNo = serialNo
  handleSearch()
}

// 清空最近查询记录
const clearRecentSerials = () => {
  recentSerials.value = []
  saveRecentSerials()
  ElMessage.success('已清空查询记录')
}

// 获取状态类型
const getStatusType = (state) => {
  return state === 1 ? 'success' : 'danger'
}

// 获取状态文本
const getStatusText = (state) => {
  return state === 1 ? '正常' : '异常'
}

// 重置查询
const resetSearch = () => {
  query.serialNo = ''
  data.value = null
}

// 查询逻辑
const handleSearch = async () => {
  // 前端校验：12位数字和字母（不区分大小写）
  if (!/^[A-Za-z0-9]{12}$/.test(query.serialNo)) {
    ElMessage.warning('请输入12位序列号')
    return
  }

  loading.value = true
  data.value = null

  try {
    // 调用API
    const response = await getSomeOne('X431 Server', 'getWebPdtStateInfo?sn=' + query.serialNo)
    
    // 检查响应状态
    if (response.data.code === 0) {
      // 成功获取数据
      data.value = response.data.data
      
      // 查询成功后添加到最近记录
      addToRecentSerials(query.serialNo)
      
      ElMessage.success('查询成功')
    } else {
      // 业务逻辑错误
      if(response.data.code === 405){
        ElMessage.error('非X431类型接头')
      }
      else{
        ElMessage.error(response.data.msg || response.data.message || '查询失败')
      }  
      data.value = null
    }
  } catch (err) {
    console.error('API请求错误:', err)
    ElMessage.error(err.message || '接口请求失败，请稍后重试')
    data.value = null
  } finally {
    loading.value = false
  }
}

// 组件挂载时加载最近查询记录
onMounted(() => {
  loadRecentSerials()
})
</script>

<style scoped>
.container {
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
}

.search-card {
  margin-bottom: 20px;
  border-radius: 8px;
}

.recent-searches {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #ebeef5;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.recent-label {
  font-size: 13px;
  color: #909399;
  white-space: nowrap;
}

.recent-tag {
  cursor: pointer;
  transition: all 0.3s;
  font-family: monospace;
}

.recent-tag:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.clear-btn {
  margin-left: auto;
}

.result-card {
  margin-top: 10px;
  border-radius: 8px;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.serial-tag {
  font-family: monospace;
}

.description-value {
  font-weight: 500;
  color: var(--wf-text-primary);
}

:global(html.dark .sn-mycar-info .search-btn) {
  color: var(--wf-status-info-text) !important;
  border-color: rgba(96, 165, 250, 0.46) !important;
  background: var(--wf-status-info-bg) !important;
  box-shadow: none !important;
}

:global(html.dark .sn-mycar-info .search-btn:hover) {
  color: #dbeafe !important;
  border-color: rgba(96, 165, 250, 0.7) !important;
  background: rgba(59, 130, 246, 0.24) !important;
}

:global(html.dark .sn-mycar-info .reset-btn) {
  color: var(--wf-text-regular) !important;
  border-color: var(--wf-control-border) !important;
  background: var(--wf-surface-soft) !important;
  box-shadow: none !important;
}

:global(html.dark .sn-mycar-info .reset-btn:hover) {
  color: var(--wf-text-primary) !important;
  border-color: #64748b !important;
  background: var(--wf-hover-bg) !important;
}

:global(html.dark .sn-mycar-info .clear-btn) {
  min-height: 26px;
  padding: 5px 10px !important;
  color: var(--wf-status-danger-text) !important;
  border-color: rgba(248, 113, 113, 0.42) !important;
  border-radius: 6px;
  background: var(--wf-status-danger-bg) !important;
  box-shadow: none !important;
}

:global(html.dark .sn-mycar-info .clear-btn:hover) {
  color: #fee2e2 !important;
  border-color: rgba(248, 113, 113, 0.68) !important;
  background: rgba(239, 68, 68, 0.24) !important;
}

/* 响应式调整 */
@media (max-width: 768px) {
  .container {
    padding: 10px;
  }
  
  .header {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
  
  .recent-searches {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .clear-btn {
    margin-left: 0;
    margin-top: 8px;
  }
}
</style>
